import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class CantemiriGiftOffer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('[data-gift-dialog]');
    this.submitButton = this.querySelector('[data-gift-submit]');
    this.errorBox = this.querySelector('[data-gift-error]');
    this.mainVariants = this.readJson('[data-main-variants]');
    this.giftVariants = this.readJson('[data-gift-variants]');
    document.body.classList.add('cm-gift-offer-active');

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onLocalClick = this.onLocalClick.bind(this);
    this.onChange = this.onChange.bind(this);

    document.addEventListener('click', this.onDocumentClick, true);
    this.addEventListener('click', this.onLocalClick);
    this.addEventListener('change', this.onChange);

    requestAnimationFrame(() => this.placeTeaser());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick, true);
    this.removeEventListener('click', this.onLocalClick);
    this.removeEventListener('change', this.onChange);
    document.body.classList.remove('cm-gift-offer-active');
  }

  readJson(selector) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || '{}');
    } catch {
      return {};
    }
  }

  placeTeaser() {
    const teaser = this.querySelector('.cm-gift-teaser');
    const price = document.querySelector('.product-information product-price');
    if (teaser && price && teaser.previousElementSibling !== price) {
      price.insertAdjacentElement('afterend', teaser);
    }
  }

  onDocumentClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const giftOpen = target.closest('[data-gift-open]');
    if (giftOpen) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.openOffer();
      return;
    }

    const addButton = target.closest('button[ref="addToCartButton"]');
    if (!addButton) return;

    const form = addButton.closest('product-form-component');
    const sticky = addButton.closest('sticky-add-to-cart');
    const formMatches = form?.dataset.productId === this.dataset.productId;
    const stickyMatches = sticky?.dataset.productId === this.dataset.productId;
    if (!formMatches && !stickyMatches) return;
    if (this.dataset.submitting === 'true') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.openOffer();
  }

  onLocalClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-gift-open]')) {
      event.preventDefault();
      this.openOffer();
      return;
    }

    if (target.closest('[data-gift-close]')) {
      this.dialog?.close();
      return;
    }

    const card = target.closest('[data-gift-card]');
    if (card) {
      event.preventDefault();
      event.stopPropagation();
      const radio = card.querySelector('[data-gift-product]');
      if (radio && !radio.disabled) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (target.closest('[data-gift-submit]')) {
      this.addBundle();
    }
  }

  onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.matches('[data-gift-product]')) this.updateSelection(input.value);
  }

  openOffer() {
    this.clearError();
    this.placeTeaser();
    this.refreshSelection();
    if (!this.dialog?.open) this.dialog?.showModal();
  }

  getMainVariantId() {
    const formInput = document.querySelector('product-form-component[data-product-id="' + this.dataset.productId + '"] input[name="id"]');
    if (formInput?.value) return String(formInput.value);
    const sticky = document.querySelector('sticky-add-to-cart[data-product-id="' + this.dataset.productId + '"]');
    return sticky?.dataset.currentVariantId || '';
  }

  getMainVariant() {
    const variantId = this.getMainVariantId();
    return Array.isArray(this.mainVariants)
      ? this.mainVariants.find((item) => String(item.id) === String(variantId))
      : null;
  }

  resolveGiftVariant(key) {
    const main = this.getMainVariant();
    if (!main) return null;

    const candidates = Array.isArray(this.giftVariants?.[key]) ? this.giftVariants[key] : [];
    return candidates.find((variant) => {
      if (!variant.available) return false;
      const sizeMatches = String(variant.size) === String(main.size);
      if (!sizeMatches) return false;
      if (key === 'rainbow') {
        return String(variant.color).toLowerCase() === String(main.color).toLowerCase();
      }
      return true;
    }) || null;
  }

  refreshSelection() {
    const selected = this.querySelector('[data-gift-product]:checked');
    if (selected) this.updateSelection(selected.value);
    else {
      if (this.submitButton) this.submitButton.disabled = true;
    }
  }

  updateSelection(key) {
    const main = this.getMainVariant();
    const gift = this.resolveGiftVariant(key);

    if (!main) {
      if (this.submitButton) this.submitButton.disabled = true;
      return;
    }

    if (!gift) {
      if (this.submitButton) this.submitButton.disabled = true;
      return;
    }

    if (this.submitButton) this.submitButton.disabled = false;
  }

  async addBundle() {
    if (this.dataset.submitting === 'true') return;

    const mainVariantId = this.getMainVariantId();
    const giftProduct = this.querySelector('[data-gift-product]:checked');
    const giftVariant = giftProduct ? this.resolveGiftVariant(giftProduct.value) : null;

    if (!mainVariantId) {
      this.showError('Choose your Pink Blossom size and color first.');
      return;
    }

    if (!giftProduct) {
      this.showError('Choose your complimentary ring.');
      return;
    }

    if (!giftVariant) {
      this.showError('This gift is not available in your selected Pink Blossom option.');
      return;
    }

    this.dataset.submitting = 'true';
    this.submitButton.disabled = true;
    const submitText = this.querySelector('[data-gift-submit-text]');
    if (submitText) submitText.textContent = 'Adding your gift…';
    this.clearError();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });

    const items = [
      { id: Number(mainVariantId), quantity: 1 },
      {
        id: Number(giftVariant.id),
        quantity: 1,
        properties: { '_Cantemiri gift': 'Pink Blossom complimentary ring' }
      }
    ];

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        { merchandiseId: String(mainVariantId), quantity: 1 },
        { merchandiseId: String(giftVariant.id), quantity: 1 }
      ],
      promise: deferred.promise
    }));

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') })
      });

      const added = await response.json();
      if (!response.ok || added.status) {
        throw new Error(added.description || added.message || 'Could not add your gift.');
      }

      const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cantemiri-gift-offer',
          sourceId: this.id,
          itemCount: 2,
          productId: this.dataset.productId,
          sections: added.sections,
          didError: false
        }
      });

      this.dialog?.close();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not add your gift.';
      this.dispatchEvent(new CartErrorEvent({
        error: message,
        code: 'INVALID',
        detail: { description: message }
      }));
      deferred.reject(error);
      this.showError(message);
    } finally {
      this.dataset.submitting = 'false';
      this.refreshSelection();
      if (submitText) submitText.textContent = 'Add both to cart';
    }
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = false;
  }

  clearError() {
    if (!this.errorBox) return;
    this.errorBox.textContent = '';
    this.errorBox.hidden = true;
  }
}

if (!customElements.get('cantemiri-gift-offer')) {
  customElements.define('cantemiri-gift-offer', CantemiriGiftOffer);
}
